<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import NotificationFlashbar from '@/domains/notifications/NotificationFlashbar.vue';
import {
  useMarkRead,
  useUnreadNotifications,
  type FormattedNotifyMsg,
} from '@/domains/notifications/queries';
import { useAccountInfoQuery } from '@/platform/identity/queries';
import { useUserStore } from '@/platform/identity/store';
import { useChrome } from '@/platform/routing/chrome';
import { Path } from '@/platform/routing/targets';
import CDAlert from '@/shared/ui/CDAlert.vue';
import { AlertTriangle } from '@celerdata/icons';
import { storeToRefs } from 'pinia';

const { t } = useI18n();
const router = useRouter();
const user = useUserStore();
const chrome = useChrome();
const { info } = storeToRefs(user);
const { data: accountInfo } = useAccountInfoQuery();
const { data: unread } = useUnreadNotifications();
const { mutate: markReadMutate } = useMarkRead();
const pendingId = ref<string | null>(null);

// --- Account-state banner --------------------------------------------------
// This top banner is derived from host accountInfo / user state. It is not a
// notice API item; backend notices render in NotificationFlashbar below.

const trialPeriod = computed(() => !accountInfo.value?.remainingTime);
const noCreditCard = computed(() => accountInfo.value?.remindCode === 1);
const isMarketplace = computed(() => !!info.value?.isMarketPlace);
const noClose = computed(() => trialPeriod.value && noCreditCard.value);
const daysLeft = computed(() =>
  trialPeriod.value ? 0 : Math.floor((accountInfo.value?.remainingTime ?? 0) / 86400) + 1,
);
const userClosed = ref(false);
const trialVisible = computed(() => {
  if (!accountInfo.value) return false;
  if (trialPeriod.value) return noCreditCard.value && !isMarketplace.value;
  return !userClosed.value;
});

// --- Notice API flashbar ---------------------------------------------------

// Sort broadcasts to the front; Array.prototype.sort is stable so non-
// broadcasts keep backend order.
const noticeList = computed<FormattedNotifyMsg[]>(() => {
  const list = unread.value?.barNotifyList?.notify_msgs ?? [];
  return [...list].sort((a, b) => (b.is_broadcast ? 1 : 0) - (a.is_broadcast ? 1 : 0));
});

function onMarkRead(item: FormattedNotifyMsg): void {
  pendingId.value = item.detail_biz_id ?? null;
  markReadMutate(item, {
    onSettled: () => {
      pendingId.value = null;
    },
  });
}

function onJump(item: FormattedNotifyMsg): void {
  const jump = item.jumpDetail;
  if (!jump?.link) return;
  if (jump.linkType === 'DOCS' || jump.open_in_new_tab) {
    window.open(jump.link);
    return;
  }
  void router.push(jump.link);
}
</script>

<template>
  <div data-testid="app-banner" class="flex flex-col">
    <CDAlert
      v-if="trialVisible"
      :type="trialPeriod ? 'error' : 'info'"
      :closable="!noClose"
      :show-icon="trialPeriod"
      :icon="AlertTriangle"
      data-testid="account-state-banner"
      @close="userClosed = true"
    >
      <template #default>
        <!-- I18nT fills {0}/{1}/… from default-slot children in order -->
        <I18nT v-if="trialPeriod" keypath="trial.period" tag="span" scope="global">
          <RouterLink :to="Path.Billing" class="underline">
            {{ t('trial.updateBilling') }}
          </RouterLink>
          <a :href="`mailto:${t('common.supportMail')}`" class="underline">
            {{ t('common.supportMail') }}
          </a>
        </I18nT>
        <I18nT v-else-if="isMarketplace" keypath="trial.marketplaceLimit" tag="span" scope="global">
          <span class="font-bold">{{ daysLeft }}</span>
        </I18nT>
        <I18nT v-else keypath="trial.limit" tag="span" scope="global">
          <span class="font-bold">{{ daysLeft }}</span>
          <RouterLink :to="Path.Billing" class="underline">
            {{ t('trial.payment') }}
          </RouterLink>
          <a :href="`mailto:${t('common.supportMail')}`" class="underline">
            {{ t('trial.chat') }}
          </a>
        </I18nT>
      </template>
    </CDAlert>

    <NotificationFlashbar
      v-if="chrome.flashbar"
      :items="noticeList"
      :pending-id="pendingId"
      @mark-read="onMarkRead"
      @jump="onJump"
    />
  </div>
</template>
